import { Stack } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import type { BuildingLayerObject, Coordinates } from "../../model/types";

import { OutlineDrawingMap } from "../../../../../components/map/OutlineDrawingMap";
import { MapDrawingToolbar } from "../shared/MapDrawingToolbar";

type LayersStepMap = {
  actions: {
    appendPointWhileDrawing: (point: [number, number]) => void;
    deleteSelectedObject: () => void;
    endDrawingSession: () => void;
    finishCurrentPolygon: () => void;
    onSelectPolygon: (objectId: string) => void;
    onToolbarDrawClick: () => void;
    onToolbarEditClick: () => void;
    updateObjectPoint: (objectId: null | string, pointIndex: number, nextPoint: [number, number]) => void;
  };
  scope: {
    canDraw: boolean;
    defaultCenter: Coordinates;
    drawingObjectId: null | string;
    mapObjects: BuildingLayerObject[];
  };
  ui: {
    isDrawing: boolean;
    isEditing: boolean;
    selectedObjectId: null | string;
  };
};

export const LayersStepMapColumn = ({ map }: { map: LayersStepMap }) => {
  const { t } = useTranslation();
  const { actions, scope, ui } = map;

  return (
    <Stack className="h-full min-h-[64rem] flex-col gap-4">
      <MapDrawingToolbar
        deleteDisabled={!ui.selectedObjectId}
        deleteLabel={t("AddBuilding.layersStep.drawToolbar.deleteObject")}
        drawLabel={t("AddBuilding.layersStep.drawToolbar.draw")}
        drawStartedNotFinished={ui.isDrawing && !!ui.selectedObjectId}
        editLabel={t("AddBuilding.layersStep.drawToolbar.editPoints")}
        isDrawing={ui.isDrawing}
        isEditing={ui.isEditing}
        onDeleteClick={actions.deleteSelectedObject}
        onDrawClick={actions.onToolbarDrawClick}
        onEditClick={actions.onToolbarEditClick}
        onEndDrawingClick={actions.finishCurrentPolygon}
        primaryButtonsDisabled={!scope.canDraw}
        showEndDrawingButton
      />
      <div className="w-full flex-1 overflow-hidden rounded-md shadow-elevation400 border-ds-subtle">
        <OutlineDrawingMap
          center={scope.defaultCenter}
          completedPolygons={scope.mapObjects}
          drawingPolygonId={scope.drawingObjectId}
          isDrawing={ui.isDrawing && scope.canDraw}
          isEditing={ui.isEditing && scope.canDraw}
          onAddPoint={actions.appendPointWhileDrawing}
          onFinish={actions.finishCurrentPolygon}
          onSelectPolygon={actions.onSelectPolygon}
          onUpdatePoint={(index: number, point: [number, number]) => {
            if (!ui.selectedObjectId) return;
            actions.updateObjectPoint(ui.selectedObjectId, index, point);
          }}
          selectedPolygonId={ui.selectedObjectId ?? undefined}
        />
      </div>
    </Stack>
  );
};
